import { useMemo } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { ROLE_MENU_MATRIX } from "@/core/admin/establishment-store";
import { buildMenuCatalog } from "@/core/navigation/menu-catalog";
import { routes } from "@/router/all_routes";
import { useAuth } from "@itaas/core/auth/useAuth";

export function AdminUsersPage() {
  const auth = useAuth();

  const visibleMenus = useMemo(() => {
    return buildMenuCatalog(auth).flatMap((section) =>
      section.items.map((item) => `${section.title} → ${item.label}`),
    );
  }, [auth]);

  return (
    <div>
      <PageHeader
        title="Utilisateurs & rôles"
        description="Session courante, permissions IAM et correspondance avec les menus Hospitol."
        actions={
          <div className="d-flex gap-2 ph-page-header-actions">
            <Link to={routes.adminSettings} className="btn btn-outline-secondary btn-sm">
              Paramètres
            </Link>
            <Link to={routes.adminMenuDemo} className="btn btn-outline-secondary btn-sm">
              Personnaliser menus
            </Link>
          </div>
        }
      />

      <div className="ph-invoice-sheet mb-4">
        <div className="ph-invoice-section-title">Utilisateur connecté</div>
        <dl className="row mb-0 small">
          <dt className="col-sm-3 text-muted">E-mail</dt>
          <dd className="col-sm-9 fw-medium">{auth.user?.email ?? "—"}</dd>
          <dt className="col-sm-3 text-muted">Nom</dt>
          <dd className="col-sm-9">
            {[auth.user?.firstName, auth.user?.lastName].filter(Boolean).join(" ") || "—"}
          </dd>
          <dt className="col-sm-3 text-muted">Permissions</dt>
          <dd className="col-sm-9">
            {auth.permissionKeys.length === 0 ? (
              <span className="text-muted">Aucune clé chargée</span>
            ) : (
              <div className="d-flex flex-wrap gap-1">
                {auth.permissionKeys.map((key) => (
                  <span key={key} className="ph-inv-badge">
                    {key}
                  </span>
                ))}
              </div>
            )}
          </dd>
        </dl>
      </div>

      <div className="ph-invoice-sheet mb-4">
        <div className="ph-invoice-section-title">Menus visibles (session)</div>
        <p className="small text-muted">
          {visibleMenus.length} entrée(s) selon vos droits IAM et le filtre démo.
        </p>
        <ul className="small mb-0 ph-admin-menu-columns">
          {visibleMenus.map((line) => (
            <li key={line} className="mb-1">
              {line}
            </li>
          ))}
        </ul>
      </div>

      <div className="ph-invoice-sheet">
        <div className="ph-invoice-section-title">Mapping rôles IAM ↔ menus</div>
        <p className="small text-muted mb-3">
          Référentiel des permissions Hospitol — la gestion fine des comptes se fait dans ITaaS IAM.
        </p>
        <div className="table-responsive">
          <table className="table table-sm ph-dash-table ph-diag-table align-middle mb-0">
            <thead>
              <tr>
                <th>Permission IAM</th>
                <th>Domaine</th>
                <th>Menus associés</th>
                <th className="text-center">Vous</th>
              </tr>
            </thead>
            <tbody>
              {ROLE_MENU_MATRIX.map((row) => {
                const hasAccess = auth.hasAnyPermission([row.permissionKey]);
                return (
                  <tr key={row.permissionKey} className="ph-diag-row">
                    <td data-label="Permission IAM">
                      <code className="small">{row.permissionKey}</code>
                    </td>
                    <td data-label="Domaine">{row.label}</td>
                    <td className="small text-muted" data-label="Menus associés">
                      {row.menuSections.join(", ")}
                    </td>
                    <td className="text-center" data-label="Vous">
                      {hasAccess ? (
                        <span className="badge bg-success-subtle text-success">Oui</span>
                      ) : (
                        <span className="badge bg-secondary-subtle text-secondary">Non</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="small text-muted mt-3 mb-0">
          Comptes et rôles hospitaliers : module IAM Suite (hors scope MVP Hospitol).
        </p>
      </div>
    </div>
  );
}
